import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { PageVisitHistoryItem } from '@/models/pageVisitHistory';
import { createPageVisitHistoryPageHeaders } from '@/pages/system/pageVisitHistory/PageVisitHistoryPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { pageVisitHistoryService } from '@/services/pageVisitHistoryService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IPageOptions } from '@/types';
import { IPageVisitHistoryFilters } from '@/types/pageVisitHistory';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createPageVisitHistoryStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<PageVisitHistoryItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IPageVisitHistoryFilters>({
        pageVisitHistoryDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'pageVisitHistory.dateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        pageVisitHistorySourceId: {
            value: 1,
            title: 'pageVisitHistory.source',
            allowSaveState: true,
            disableClear: true,
        },
        pageVisitHistoryPageId: {
            value: null,
            title: 'pageVisitHistory.page',
            allowSaveState: true,
        },
        pageVisitHistoryToolUserId: {
            value: null,
            title: 'pageVisitHistory.user',
            allowSaveState: true,
        },
        pageVisitHistoryUniqAppId: {
            value: null,
            title: 'pageVisitHistory.uniqAppId',
            allowSaveState: true,
        },
        pageVisitHistorySearch: {
            value: null,
            title: 'pageVisitHistory.search',
            allowSaveState: false,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.pageVisitHistoryDate.value = todayDateRangeInUserTimeZone();
        filters.pageVisitHistorySourceId.value = 1;
        filters.pageVisitHistoryPageId.value = null;
        filters.pageVisitHistoryToolUserId.value = null;
        filters.pageVisitHistoryUniqAppId.value = null;
        filters.pageVisitHistorySearch.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createPageVisitHistoryPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'pageVisitHistory',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            if (filters.pageVisitHistoryUniqAppId.value) {
                filtersToSend.pageVisitHistoryDate = { ...filtersToSend.pageVisitHistoryDate, value: null };
            }

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Page'],
            };

            const response = await pageVisitHistoryService.list(params);

            items.value = (response.data.rows || []).map((row) => new PageVisitHistoryItem(row));
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        stats: undefined,
    };
}

export function usePageVisitHistoryStore(storeId: string) {
    const useStore = defineStore(storeId, () => createPageVisitHistoryStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'pageVisitHistory'),
    });
    return useStore();
}
